import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Users, Loader2, Eye, EyeOff, Key, SlidersHorizontal, ExternalLink } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import GestorModulesDialog from '@/components/GestorModulesDialog';
import ProvidersAdminPanel from '@/components/admin/ProvidersAdminPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GestoresPage = () => {
  const { getAuthHeaders } = useAuth();
  const [gestores, setGestores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedGestor, setSelectedGestor] = useState(null);
  const [modulesDialogGestor, setModulesDialogGestor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchGestores();
  }, []);

  const fetchGestores = async () => {
    try {
      const response = await axios.get(`${API_URL}/gestores`, {
        headers: getAuthHeaders()
      });
      setGestores(response.data);
    } catch (error) {
      toast.error('Error al cargar gestores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = { ...formData };
      // Don't send empty password on edit (only update if provided)
      if (selectedGestor && !payload.password) {
        delete payload.password;
      }
      
      if (selectedGestor) {
        await axios.put(`${API_URL}/gestores/${selectedGestor.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Gestor actualizado');
      } else {
        await axios.post(`${API_URL}/gestores`, payload, {
          headers: getAuthHeaders()
        });
        toast.success('Gestor creado con acceso');
      }
      fetchGestores();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/gestores/${selectedGestor.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Gestor eliminado');
      fetchGestores();
      setIsDeleteDialogOpen(false);
      setSelectedGestor(null);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (gestor) => {
    setSelectedGestor(gestor);
    setFormData({
      name: gestor.name,
      email: gestor.email,
      phone: gestor.phone || '',
      password: ''
    });
    setShowPassword(false);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedGestor(null);
    setFormData({ name: '', email: '', phone: '', password: '' });
    setShowPassword(false);
  };

  const filteredGestores = gestores.filter(g => 
    g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="gestores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestores de Ventas
          </h1>
          <p className="text-slate-500 mt-1">Administra tu equipo de ventas</p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButton type="table" tableName="gestores" />
          <Button 
            onClick={() => setIsDialogOpen(true)}
            className="bg-blue-700 hover:bg-blue-800"
            data-testid="add-gestor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Gestor
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Gestores</p>
              <p className="text-2xl font-bold text-slate-900">{gestores.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Activos</p>
              <p className="text-2xl font-bold text-slate-900">{gestores.filter(g => g.is_active).length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Users className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Ventas Totales</p>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(gestores.reduce((sum, g) => sum + g.total_sales, 0))}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Buscar por nombre o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white"
          data-testid="search-gestores"
        />
      </div>

      {/* Table */}
      <Card className="shadow-sm" data-testid="gestores-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Gestor</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ventas</TableHead>
                <TableHead className="text-right">Pasajes</TableHead>
                <TableHead className="w-40">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGestores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No se encontraron gestores
                  </TableCell>
                </TableRow>
              ) : (
                filteredGestores.map((gestor) => (
                  <TableRow key={gestor.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600">
                          {gestor.name.charAt(0)}
                        </div>
                        <span className="font-medium text-slate-900">{gestor.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{gestor.email}</TableCell>
                    <TableCell className="text-slate-600">{gestor.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={gestor.is_active ? 'default' : 'secondary'} className={gestor.is_active ? 'bg-green-100 text-green-800' : ''}>
                        {gestor.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">
                      {formatCurrency(gestor.total_sales)}
                    </TableCell>
                    <TableCell className="text-right text-slate-600">
                      {gestor.total_tickets}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Configurar módulos"
                          onClick={() => setModulesDialogGestor(gestor)}
                          data-testid={`modules-gestor-${gestor.id}`}
                        >
                          <SlidersHorizontal className="w-4 h-4 text-amber-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Vista previa como gestor"
                          onClick={async () => {
                            try {
                              const r = await axios.post(
                                `${API_URL}/admin/gestores/${gestor.id}/preview-token`,
                                {},
                                { headers: getAuthHeaders() },
                              );
                              const url = `${window.location.origin}/preview/launch?token=${encodeURIComponent(r.data.token)}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            } catch (e) {
                              toast.error('No se pudo abrir la vista previa');
                            }
                          }}
                          data-testid={`preview-gestor-${gestor.id}`}
                        >
                          <ExternalLink className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(gestor)}
                          data-testid={`edit-gestor-${gestor.id}`}
                        >
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedGestor(gestor);
                            setIsDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-gestor-${gestor.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Providers admin (payment_agents + ticket_providers) */}
      <ProvidersAdminPanel />

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedGestor ? 'Editar Gestor' : 'Nuevo Gestor'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre completo *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-slate-50"
                data-testid="gestor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-slate-50"
                data-testid="gestor-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-slate-50"
                data-testid="gestor-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Key className="w-4 h-4 text-slate-500" />
                {selectedGestor ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña de Acceso *'}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!selectedGestor}
                  placeholder={selectedGestor ? 'Dejar vacío para mantener actual' : 'Contraseña para el gestor'}
                  className="bg-slate-50 pr-10"
                  data-testid="gestor-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </Button>
              </div>
              {!selectedGestor && (
                <p className="text-xs text-slate-500">
                  Esta contraseña le permitirá al gestor acceder a la plataforma
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-blue-700 hover:bg-blue-800"
                data-testid="save-gestor-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que deseas eliminar a <strong>{selectedGestor?.name}</strong>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modules Configuration Dialog */}
      <GestorModulesDialog
        gestor={modulesDialogGestor}
        open={!!modulesDialogGestor}
        onOpenChange={(o) => { if (!o) setModulesDialogGestor(null); }}
      />
    </div>
  );
};

export default GestoresPage;
